<template>
  <BaseDetails class="base-details" v-if="showCandidateSourcing">
    <template v-slot:header>
      <h2 class="pb-3">Candidate Sourcing</h2>
    </template>
    <v-skeleton-loader v-if="isLoadingSchedules" type="list-item-three-line" />
    <div v-for="(scheduleGroup, tierName) in groupedSchedules" :key="tierName">
      <p class="subsection-title">{{ tierName }}</p>
      <PublishScheduleItem
        v-for="schedule in scheduleGroup"
        :key="schedule.id"
        :cost="schedule.cost"
        :currencyCode="schedule.currencyCode"
        :label="getReceiverName(schedule.receiver)"
        :publishingDate="schedule.publishOnDate"
        :status="schedule.state"
        :unitId="schedule.id"
        :url="schedule.url"
        @updated="handleUpdate"
      />
    </div>
    <Permissioned section="bookings">
      <PrimaryButton
        class="mx-1"
        permission="edit.publishing-channels"
        @click.native="dialogSettings.dialog = true"
        full-width
        light
      >
        Add Channel
        <v-icon>mdi-plus</v-icon>
      </PrimaryButton>
    </Permissioned>
    <ValidationObserver ref="newPublishingSchedule">
      <Dialog :data="dialogSettings">
        <p class="secondary-text">
          If the role is live, it will be published to this channel immediately
        </p>
        <ValidationProvider v-slot="{ errors }" name="channel" rules="required">
          <Select
            item-text="channelName"
            item-value="id"
            v-model="newChannelId"
            :items="filteredChannels"
            :loading="isFetchingChannels"
            label=""
            placeholder="Select Channel"
            :error-messages="showValidationErrors ? errors : ''"
          />
        </ValidationProvider>
        <template v-slot:dialog.actions>
          <PrimaryButton @click.native="dialogSettings.dialog = false" text>
            Cancel
          </PrimaryButton>
          <PrimaryButton @click.native="addChannel" :loading="isAdding">
            Add Channel
          </PrimaryButton>
        </template>
      </Dialog>
    </ValidationObserver>
  </BaseDetails>
</template>

<script>
import BaseDetails from "@/components/common/BaseDetails/BaseDetails";
import PublishScheduleItem from "@/components/bookings/PublishScheduleItem";
import { createNamespacedHelpers } from "vuex";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
  FETCH_PUBLISHING_CHANNELS,
  CREATE_PUBLISHING_SCHEDULE
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
  IS_LOADING_BOOKING_PUBLISHING_SCHEDULE,
  GET_PUBLISHING_CHANNELS,
  GET_IS_FETCHING_PUBLISHING_CHANNELS
} from "@/store/modules/bookings/getters";
import { groupByPropertyName } from "@/utils/bookings";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import Dialog from "@/components/common/Dialog";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Select from "@/components/common/Select";
import Permissioned from "@/components/common/Permissioned";
import { reduce, map } from "lodash";

const { mapActions, mapGetters } = createNamespacedHelpers(BOOKINGS_NAMESPACE);

export default {
  name: "PublishingSchedule",
  components: {
    PublishScheduleItem,
    BaseDetails,
    PrimaryButton,
    Dialog,
    ValidationObserver,
    ValidationProvider,
    Select,
    Permissioned
  },
  props: {
    bookingId: {
      type: String,
      required: true
    },
    bookingStatus: String
  },
  async created() {
    await this.fetchPublishingSchedule(this.bookingId);
    this.fetchPublishingChannels({});
  },
  data() {
    return {
      dialogSettings: {
        dialog: false,
        title: "Add Pubslshing Channel"
      },
      newChannelId: "",
      showValidationErrors: false,
      isAdding: false
    };
  },
  computed: {
    ...mapGetters({
      isLoading: IS_LOADING_BOOKING_PUBLISHING_SCHEDULE,
      publishingSchedules: GET_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
      channels: GET_PUBLISHING_CHANNELS,
      isFetchingChannels: GET_IS_FETCHING_PUBLISHING_CHANNELS
    }),
    groupedSchedules() {
      return groupByPropertyName(this.publishingSchedules, "tierName");
    },
    showCandidateSourcing() {
      return this.bookingStatus === "live";
    },
    isLoadingSchedules() {
      return this.isLoading && !this.publishingSchedules.length;
    },
    filteredChannels() {
      const filtered = reduce(
        this.channels,
        (filteredChannels, channel) => {
          filteredChannels.push(channel);
          map(this.publishingSchedules, ({ receiver }) => {
            if (receiver.id === channel.id) {
              filteredChannels.pop();
            }
          });
          return filteredChannels;
        },
        []
      );
      return filtered;
    }
  },
  methods: {
    ...mapActions({
      fetchPublishingSchedule: FETCH_CURRENT_BOOKING_PUBLISHING_SCHEDULE,
      fetchPublishingChannels: FETCH_PUBLISHING_CHANNELS,
      createPublishingSchedule: CREATE_PUBLISHING_SCHEDULE
    }),
    handleUpdate() {
      this.fetchPublishingSchedule(this.bookingId);
    },
    isConfirmed(schedule) {
      return schedule.state !== "pending";
    },
    getReceiverName(receiver) {
      if (receiver) {
        return receiver.channelName || receiver.name;
      }
      return "";
    },
    async addChannel() {
      this.isAdding = true;
      this.showValidationErrors = true;
      const isValid = this.$refs.newPublishingSchedule.validate();
      if (isValid) {
        await this.createPublishingSchedule({
          bookingId: this.bookingId,
          channel_id: this.newChannelId
        });
      }
      this.fetchPublishingSchedule(this.bookingId);
      this.isAdding = false;
      this.dialogSettings.dialog = false;
    }
  }
};
</script>

<style lang="scss">
.base-details {
  .subsection-title {
    font-size: 1.1rem;
    margin-top: 10px;
    margin-bottom: 5px;
  }

  .caption {
    margin-bottom: 0;
  }
}
</style>
